import React, { useEffect, useState } from "react";
import { Form, Input, Button, message, DatePicker, Select, Row, Col, Card, Typography, Divider, Modal } from "antd";
import API from "../../api";
import moment from "moment";

const { Title, Text } = Typography;
const { Option } = Select;

const EmployeeSalary = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [paymentStatus, setPaymentStatus] = useState('idle'); // 'idle', 'processing', 'success', 'failed'
    const [submissionAttempts, setSubmissionAttempts] = useState(0);
    const [stores, setStores] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [calculatedSalary, setCalculatedSalary] = useState(0);
    const [paymentType, setPaymentType] = useState('Salary');
    const [existingAdvances, setExistingAdvances] = useState(0);
    const [outstandingBalance, setOutstandingBalance] = useState(0);
    const [totalOutstanding, setTotalOutstanding] = useState(0);
    const [previousMonthDetails, setPreviousMonthDetails] = useState([]);
    const [remainingAmount, setRemainingAmount] = useState(0);
    const [paymentHistory, setPaymentHistory] = useState([]);
    const [paymentHistoryLoading, setPaymentHistoryLoading] = useState(false);
    const [advanceDeduction, setAdvanceDeduction] = useState(0);
    const [paidAmount, setPaidAmount] = useState(0);
    const [balanceAmount, setBalanceAmount] = useState(0);

    // Fetch stores
    const getStores = async () => {
        try {
            const response = await API.get("/stores");
            setStores(response?.data || []);
            console.log('✅ Stores loaded:', response?.data);
        } catch (err) {
            console.error("Error fetching stores:", err);
            message.error("Failed to load stores.");
        }
    };

    // Fetch employees by store
    const getEmployeesByStore = async (storeId) => {
        if (!storeId) return;
        try {
            const response = await API.get(`/employee/by-store`, {
                params: {
                    storeId: storeId
                }
            });
            setEmployees(response?.data || []);
        } catch (err) {
            console.error("Error fetching employees:", err);
            message.error("Failed to load employees.");
        }
    };

    // Handle store change
    const handleStoreChange = (storeId) => {
        form.setFieldsValue({ employeeId: undefined });
        setEmployees([]);
        setSelectedEmployee(null);
        setCalculatedSalary(0);
        setExistingAdvances(0);
        setOutstandingBalance(0);
        setTotalOutstanding(0);
        setPreviousMonthDetails([]);
        setRemainingAmount(0);
        setPaymentHistory([]);
        setAdvanceDeduction(0);
        setPaidAmount(0);
        setBalanceAmount(0);
        setSubmissionAttempts(0);
        setPaymentStatus('idle');
        
        // Maintain current year and month when changing store
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().toLocaleString('default', { month: 'long' });
        form.setFieldsValue({ 
            year: currentYear,
            month: currentMonth,
            totalDays: 30, // Fixed total days
            advanceDeduction: 0,
            paidAmount: 0,
            balanceAmount: 0,
            paymentMode: 'Cash'
        });
        
        if (storeId) {
            getEmployeesByStore(storeId);
        }
    };

    // Handle employee change
    const handleEmployeeChange = async (employeeId) => {
        console.log('👤 Employee selected:', employeeId);
        
        // Set the form value for employeeId
        form.setFieldsValue({ employeeId: employeeId });
        
        const employee = employees.find(emp => emp._id === employeeId);
        setSelectedEmployee(employee);
        
        if (employee?.salary) {
            form.setFieldsValue({ currentSalary: employee.salary });
        }
        setCalculatedSalary(0);
        
        // Fetch existing advances and outstanding balance for this employee
        if (employeeId) {
            try {
                // Get current month and year from form or use defaults
                const currentYear = new Date().getFullYear();
                const currentMonth = new Date().toLocaleString('default', { month: 'long' });
                
                // Build query parameters for month-specific advances
                const queryParams = new URLSearchParams({
                    month: currentMonth,
                    year: currentYear.toString()
                });
                
                const response = await API.get(`/salary-payments/advances/${employeeId}?${queryParams}`);
                if (response.data.success) {
                    const data = response.data.data;
                    const totalAdvances = data.totalAdvances || 0;
                    const outstandingBalance = data.outstandingBalance || 0;
                    const totalOutstanding = data.totalOutstanding || 0;
                    const previousMonthDetails = data.previousMonthDetails || [];
                    
                    setExistingAdvances(totalAdvances);
                    setOutstandingBalance(outstandingBalance);
                    setTotalOutstanding(totalOutstanding);
                    setPreviousMonthDetails(previousMonthDetails);
                    
                    // Set fixed total days to 30
                    form.setFieldsValue({ totalDays: 30 });
                    
                    // Auto-calculate total outstanding deduction (advances + previous balances)
                    if (totalOutstanding > 0) {
                        autoCalculateAdvanceDeduction();
                        if (outstandingBalance > 0) {
                            message.info(`Outstanding balance of ₹${outstandingBalance} from previous months will be automatically deducted along with advances of ₹${totalAdvances} for ${currentMonth} ${currentYear}.`);
                        } else {
                            message.info(`Advance deduction automatically set to ₹${totalAdvances} for ${currentMonth} ${currentYear}. You can modify this amount if needed.`);
                        }
                    }
                }
            } catch (err) {
                console.error("Error fetching advances and balance:", err);
                // Set default total days to 30 even if API fails
                form.setFieldsValue({ totalDays: 30 });
            }
            
            // Fetch payment history for this employee
            fetchPaymentHistory(employeeId);
        }
    };

    // Handle payment type change
    const handlePaymentTypeChange = (type) => {
        setPaymentType(type);
        setCalculatedSalary(0);
        setRemainingAmount(0);
        
        // Maintain current year, month and other defaults
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().toLocaleString('default', { month: 'long' });
        form.setFieldsValue({
            totalDays: 30, // Fixed total days
            presentDays: undefined,
            incentiveAmount: undefined,
            deductionAmount: 0,
            advanceDeduction: 0,
            paidAmount: 0,
            balanceAmount: 0,
            paymentMode: 'Cash',
            year: currentYear,
            month: currentMonth
        });
        setAdvanceDeduction(0);
        setPaidAmount(0);
        setBalanceAmount(0);
        setOutstandingBalance(0);
        setTotalOutstanding(0);
        setPreviousMonthDetails([]);
        setSubmissionAttempts(0);
        setPaymentStatus('idle');
    };

    // Calculate salary
    const calculateSalary = () => {
        const currentSalary = selectedEmployee?.salary;
        const totalDays = 30; // Fixed total days
        const presentDays = form.getFieldValue('presentDays');
        
        if (currentSalary && presentDays) {
            // Validate present days
            if (presentDays > totalDays) {
                message.error('Present days cannot exceed 30 days');
                form.setFieldsValue({ presentDays: totalDays });
                return;
            }
            
            if (presentDays < 0) {
                message.error('Present days cannot be negative');
                form.setFieldsValue({ presentDays: 0 });
                return;
            }
            
            const dailyRate = currentSalary / totalDays;
            const calculated = dailyRate * presentDays;
            
            // Calculate deduction amount based on absent days
            const absentDays = totalDays - presentDays;
            const deductionAmount = dailyRate * absentDays;
            
            // Set the deduction amount in the form
            form.setFieldsValue({ deductionAmount: Math.round(deductionAmount) });
            
            // For salary payments, the calculated amount is the final amount
            setCalculatedSalary(Math.round(calculated));
            
            // Calculate final amount after all deductions and previous balance
            // For salary payments: Present Days Salary - Absent Days Deduction - New Advances + Previous Balance
            let finalAmount;
            
            if (paymentType === 'Salary') {
                // Salary calculation: Present Days Salary - Absent Days Deduction - New Advances + Previous Balance
                const newAdvances = existingAdvances; // Only new advances for current month
                const previousBalance = outstandingBalance; // Previous month's unpaid balance
                
                finalAmount = Math.round(calculated) - Math.round(deductionAmount) - newAdvances + previousBalance;
                
                console.log('💰 Salary Calculation Breakdown:');
                console.log('  - Present Days Salary:', calculated);
                console.log('  - Absent Days Deduction:', deductionAmount);
                console.log('  - New Advances:', newAdvances);
                console.log('  - Previous Balance:', previousBalance);
                console.log('  - Final Amount:', finalAmount);
                
                // Ensure final amount is not negative
                if (finalAmount < 0) {
                    // If total deductions exceed calculated salary + previous balance, set final amount to 0
                    setRemainingAmount(0);
                    const carryForwardAmount = Math.abs(finalAmount);
                    message.warning(`Total deductions exceed available amount. ₹${carryForwardAmount} will be carried forward.`);
                } else {
                    setRemainingAmount(finalAmount);
                    // Auto-set paid amount to net payable amount
                    autoSetPaidAmount();
                }
            } else {
                // For Advance and Incentive payments, just set the calculated amount
                finalAmount = Math.round(calculated);
                setRemainingAmount(finalAmount);
                autoSetPaidAmount();
            }
        }
    };

    // Handle advance amount change
    const handleAdvanceAmountChange = (e) => {
        const advanceAmount = parseFloat(e.target.value) || 0;
        calculateSalary(); // Recalculate with new advance amount
    };

    // Handle advance deduction change
    const handleAdvanceDeductionChange = (e) => {
        const deductionAmount = parseFloat(e.target.value) || 0;
        console.log('📝 Advance deduction changed by user:', deductionAmount);
        setAdvanceDeduction(deductionAmount);
        form.setFieldsValue({ advanceDeduction: deductionAmount });
        calculateSalary(); // Recalculate with new advance deduction
    };

    // Auto-calculate total outstanding deduction (advances + previous balances)
    const autoCalculateAdvanceDeduction = () => {
        if (totalOutstanding > 0) {
            console.log('🔄 Auto-calculating total outstanding deduction:', totalOutstanding);
            setAdvanceDeduction(totalOutstanding);
            form.setFieldsValue({ advanceDeduction: totalOutstanding });
            
            // Use setTimeout to ensure the form value is set before calculating salary
            setTimeout(() => {
                console.log('⏰ Delayed calculation with total outstanding deduction:', totalOutstanding);
                calculateSalary(); // Recalculate with auto-set total outstanding deduction
            }, 100);
        }
    };

    // Handle paid amount change
    const handlePaidAmountChange = (e) => {
        const amount = parseFloat(e.target.value) || 0;
        setPaidAmount(amount);
        
        // Calculate balance amount
        const balance = remainingAmount - amount;
        setBalanceAmount(balance);
        form.setFieldsValue({ balanceAmount: balance });
        
        console.log('💰 Paid amount changed:', amount, 'Balance:', balance);
    };

    // Auto-set paid amount to net payable amount when it changes
    const autoSetPaidAmount = () => {
        if (remainingAmount > 0) {
            console.log('🔄 Auto-setting paid amount to:', remainingAmount);
            setPaidAmount(remainingAmount);
            setBalanceAmount(0);
            form.setFieldsValue({ 
                paidAmount: remainingAmount,
                balanceAmount: 0
            });
            
            // Ensure the form field is updated
            setTimeout(() => {
                form.setFieldsValue({ 
                    paidAmount: remainingAmount,
                    balanceAmount: 0
                });
            }, 50);
        }
    };

    // Handle reference number change
    const handleReferenceNumberChange = (e) => {
        // This function can be used for additional validation if needed
    };



    // Refresh advances for a specific month and year
    const refreshAdvancesForMonth = async (month, year) => {
        if (!selectedEmployee?._id) return;
        
        try {
            console.log(`🔄 Refreshing advances for ${month} ${year}`);
            
            // Build query parameters for month-specific advances
            const queryParams = new URLSearchParams({
                month: month,
                year: year.toString()
            });
            
            const response = await API.get(`/salary-payments/advances/${selectedEmployee._id}?${queryParams}`);
            if (response.data.success) {
                const data = response.data.data;
                const totalAdvances = data.totalAdvances || 0;
                const outstandingBalance = data.outstandingBalance || 0;
                const totalOutstanding = data.totalOutstanding || 0;
                const previousMonthDetails = data.previousMonthDetails || [];
                
                setExistingAdvances(totalAdvances);
                setOutstandingBalance(outstandingBalance);
                setTotalOutstanding(totalOutstanding);
                setPreviousMonthDetails(previousMonthDetails);
                
                // Recalculate salary with new advance amounts
                if (totalOutstanding > 0) {
                    autoCalculateAdvanceDeduction();
                    message.info(`Advances updated for ${month} ${year}: ₹${totalAdvances}. Total outstanding: ₹${totalOutstanding}`);
                } else {
                    setAdvanceDeduction(0);
                    form.setFieldsValue({ advanceDeduction: 0 });
                    message.info(`No advances found for ${month} ${year}`);
                }
                
                // Recalculate salary
                calculateSalary();
            }
        } catch (err) {
            console.error("Error refreshing advances for month:", err);
            message.error("Failed to refresh advances for selected month");
        }
    };

    // Fetch payment history for employee
    const fetchPaymentHistory = async (employeeId) => {
        if (!employeeId) return;
        
        setPaymentHistoryLoading(true);
        try {
            const response = await API.get('/salary-payments', {
                params: {
                    employeeId: employeeId,
                    limit: 50 // Get last 50 payments
                }
            });
            
            if (response.data.success) {
                setPaymentHistory(response.data.data || []);
            }
        } catch (err) {
            console.error("Error fetching payment history:", err);
            setPaymentHistory([]);
        } finally {
            setPaymentHistoryLoading(false);
        }
    };

    useEffect(() => {
        getStores();
        
        // Set default values when component mounts
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().toLocaleString('default', { month: 'long' });
        form.setFieldsValue({ 
            totalDays: 30, // Fixed total days
            year: currentYear,
            month: currentMonth,
            advanceDeduction: 0,
            paidAmount: 0,
            balanceAmount: 0,
            paymentMode: 'Cash'
        });
        
        // Auto-set paid amount when remaining amount is available
        if (remainingAmount > 0) {
            autoSetPaidAmount();
        }
    }, [remainingAmount]);

    // Watch for changes in totalOutstanding and auto-set advance deduction
    useEffect(() => {
        if (totalOutstanding > 0) {
            console.log('👀 totalOutstanding changed, setting advance deduction:', totalOutstanding);
            setAdvanceDeduction(totalOutstanding);
            form.setFieldsValue({ advanceDeduction: totalOutstanding });
            
            // Also update the form field value to ensure it's displayed
            setTimeout(() => {
                form.setFieldsValue({ advanceDeduction: totalOutstanding });
            }, 50);
        }
    }, [totalOutstanding]);

    // Reset form and state
    const resetForm = () => {
        form.resetFields();
        setSelectedEmployee(null);
        setCalculatedSalary(0);
        setExistingAdvances(0);
        setOutstandingBalance(0);
        setTotalOutstanding(0);
        setPreviousMonthDetails([]);
        setRemainingAmount(0);
        setPaymentType('Salary');
        setPaymentHistory([]);
        setAdvanceDeduction(0);
        setPaidAmount(0);
        setBalanceAmount(0);
        setSubmissionAttempts(0);
        setPaymentStatus('idle');
        
        // Set default values
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().toLocaleString('default', { month: 'long' });
        form.setFieldsValue({ 
            totalDays: 30, // Fixed total days
            deductionAmount: 0,
            advanceDeduction: 0,
            paidAmount: 0,
            balanceAmount: 0,
            paymentMode: 'Cash',
            year: currentYear,
            month: currentMonth
        });
    };

    const onFinish = async (values) => {
        console.log('🚀 onFinish triggered with values:', values);
        console.log('🔍 Current paymentType:', paymentType);
        console.log('👤 Selected employee:', selectedEmployee);
        
        // Validate that month and year are not in the future
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth();
        
        const selectedMonth = values.month;
        const selectedYear = values.year;
        
        if (selectedYear > currentYear) {
            message.error('Cannot process payments for future years');
            return;
        }
        
        if (selectedYear === currentYear) {
            const selectedMonthIndex = monthOptions.indexOf(selectedMonth);
            if (selectedMonthIndex > currentMonth) {
                message.error('Cannot process payments for future months');
                return;
            }
        }
        
        // Validate paid amount only for Salary payments
        if (paymentType === 'Salary' && (!values.paidAmount || values.paidAmount <= 0)) {
            message.error('Paid amount must be greater than 0 for salary payments');
            return;
        }
        
        // Show confirmation dialog
        const confirmed = await new Promise((resolve) => {
            Modal.confirm({
                title: 'Confirm Payment',
                content: (
                    <div>
                        <p><strong>Employee:</strong> {selectedEmployee?.name}</p>
                        <p><strong>Payment Type:</strong> {paymentType}</p>
                        {paymentType === 'Salary' && (
                            <>
                                <p><strong>Net Payable:</strong> ₹{new Intl.NumberFormat().format(remainingAmount)}</p>
                                <p><strong>Paid Amount:</strong> ₹{new Intl.NumberFormat().format(values.paidAmount)}</p>
                                <p><strong>Balance Amount:</strong> ₹{new Intl.NumberFormat().format(values.balanceAmount || 0)}</p>
                                {(existingAdvances > 0 || outstandingBalance > 0) && (
                                    <>
                                        <p><strong>Calculation Details:</strong></p>
                                        <p style={{ marginLeft: '20px', fontSize: '12px', color: '#666' }}>
                                            {existingAdvances > 0 && `• New Advances for ${form.getFieldValue('month')} ${form.getFieldValue('year')}: ₹${new Intl.NumberFormat().format(existingAdvances)}`}<br />
                                            {outstandingBalance > 0 && `• Previous Balance: +₹${new Intl.NumberFormat().format(outstandingBalance)}`}
                                        </p>
                                    </>
                                )}
                            </>
                        )}
                        {paymentType === 'Advance' && (
                            <p><strong>Advance Amount:</strong> ₹{new Intl.NumberFormat().format(values.advanceAmount || 0)}</p>
                        )}
                        {paymentType === 'Incentive' && (
                            <p><strong>Incentive Amount:</strong> ₹{new Intl.NumberFormat().format(values.incentiveAmount || 0)}</p>
                        )}
                        <p><strong>Payment Mode:</strong> {values.paymentMode || 'Not selected'}</p>
                        <p>Are you sure you want to process this payment?</p>
                    </div>
                ),
                okText: 'Process Payment',
                cancelText: 'Cancel',
                onOk: () => resolve(true),
                onCancel: () => resolve(false),
            });
        });
        
        if (!confirmed) {
            return;
        }
        
        setLoading(true);
        setPaymentStatus('processing');
        try {
            const currentMonthName = currentDate.toLocaleString('default', { month: 'long' });

            const paymentData = {
                storeId: values.storeId,
                employee_Id: values.employeeId,
                paymentType: paymentType,
                for_the_month: values.month || currentMonthName,
                year: values.year || currentYear,
                net_salary: selectedEmployee?.salary || 0,
                deducted_amount: values.deductionAmount || 0,
                incentive_amount: values.incentiveAmount || 0,
                total_days: values.totalDays || 0,
                present_days: values.presentDays || 0,
                payment_date: values.paymentDate ? values.paymentDate.toISOString() : currentDate.toISOString(),
                advance_amount: paymentType === 'Advance' ? (values.advanceAmount || 0) : 0,
                advance_balance: 0, // Will be calculated based on previous advances
                advance_deduction: advanceDeduction || 0, // New field for advance deduction
                paid_amount: values.paidAmount || 0, // New field for paid amount
                balance_amount: values.balanceAmount || 0, // New field for balance amount
                payment_mode: values.paymentMode || null, // New field for payment mode
                reference_number: values.referenceNumber || null, // New field for reference number
                remarks: values.remarks || null
            };

            console.log('📤 Sending payment data:', paymentData);
            console.log('🌐 Making API call to:', '/salary-payments');

            // Add timeout to prevent hanging requests
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

            const response = await API.post('/salary-payments', paymentData, {
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            // Enhanced response validation
            if (!response || !response.data) {
                throw new Error('Invalid response from server');
            }

            if (!response.data.success) {
                throw new Error(response.data.message || 'Payment processing failed on server');
            }

            console.log('✅ API response:', response.data);
            
            // Show success message with payment details
            const successMessage = `${paymentType} payment of ₹${new Intl.NumberFormat().format(
                paymentType === 'Salary' ? (values.paidAmount || 0) :
                paymentType === 'Advance' ? (values.advanceAmount || 0) :
                (values.incentiveAmount || 0)
            )} processed successfully!`;
            
            message.success(successMessage);
            setPaymentStatus('success');
            
            // Refresh payment history for the current employee
            if (selectedEmployee?._id) {
                await fetchPaymentHistory(selectedEmployee._id);
            }
            
            // Reset form after successful payment
            resetForm();
            
        } catch (err) {
            console.error('❌ Error in onFinish:', err);
            
            // Enhanced error handling with specific messages
            let errorMessage = "Failed to process payment.";
            
            if (err.name === 'AbortError') {
                errorMessage = "Request timed out. Please try again.";
            } else if (err.response) {
                // Server responded with error status
                const { status, data } = err.response;
                console.error(`❌ Server Error [${status}]:`, data);
                
                if (status === 400) {
                    errorMessage = data.message || "Invalid payment data. Please check your inputs.";
                } else if (status === 401) {
                    errorMessage = "Authentication failed. Please login again.";
                } else if (status === 403) {
                    errorMessage = "You don't have permission to process payments.";
                } else if (status === 404) {
                    errorMessage = "Payment service not found.";
                } else if (status === 409) {
                    errorMessage = data.message || "Payment conflict. This payment may already exist.";
                } else if (status >= 500) {
                    errorMessage = "Server error. Please try again later.";
                } else {
                    errorMessage = data.message || `Payment failed (${status}). Please try again.`;
                }
            } else if (err.request) {
                // Network error or no response
                console.error('❌ Network Error:', err.request);
                errorMessage = "Network error. Please check your connection and try again.";
            } else {
                // Other errors
                console.error('❌ Unexpected Error:', err.message);
                errorMessage = err.message || "An unexpected error occurred. Please try again.";
            }
            
            message.error(errorMessage);
            setPaymentStatus('failed');
            
            // Increment submission attempts for retry logic
            setSubmissionAttempts(prev => prev + 1);
            
            // Show retry option for certain errors
            if (err.response?.status >= 500 || err.name === 'AbortError') {
                Modal.confirm({
                    title: 'Payment Failed',
                    content: 'Would you like to retry the payment?',
                    okText: 'Retry',
                    cancelText: 'Cancel',
                    onOk: () => {
                        // Retry the payment
                        setTimeout(() => {
                            onFinish(values);
                        }, 1000);
                    }
                });
            }
            
        } finally {
            setLoading(false);
            
            // Reset submission attempts on success or after a delay
            if (submissionAttempts > 0) {
                setTimeout(() => {
                    setSubmissionAttempts(0);
                }, 5000); // Reset after 5 seconds
            }
            
            // Reset payment status after a delay
            if (paymentStatus === 'success' || paymentStatus === 'failed') {
                setTimeout(() => {
                    setPaymentStatus('idle');
                }, 3000); // Reset after 3 seconds
            }
        }
    };

    const monthOptions = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const yearOptions = Array.from({ length: 2 }, (_, i) => new Date().getFullYear() - 1 + i);

    return (
        <div style={{
            padding: "20px",
            background: "#fff",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
        }}>
            <Title level={3} style={{ marginBottom: "24px" }}>Employee Payment Management</Title>
            
            <Row gutter={24}>
                <Col span={14}>
                    <Form 
                        layout="vertical" 
                        form={form} 
                        onFinish={onFinish} 
                        onFinishFailed={(errorInfo) => {
                            console.log('❌ Form validation failed:', errorInfo);
                            
                            // Show detailed validation errors
                            errorInfo.errorFields.forEach(field => {
                                console.log(`❌ Field: ${field.name}, Error: ${field.errors}`);
                                message.error(`Validation Error: ${field.errors.join(', ')}`);
                            });
                            
                            // Show summary error
                            message.error(`Please fix ${errorInfo.errorFields.length} validation error(s) before submitting.`);
                        }}
                        autoComplete="off"
                    >
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            label="Select Store/Branch"
                            name="storeId"
                            rules={[{ required: true, message: "Please select a store." }]}
                        >
                            <Select
                                placeholder="Select Store"
                                allowClear
                                onChange={handleStoreChange}
                            >
                                {stores.map((store) => (
                                    <Select.Option key={store._id} value={store._id}>
                                        {store.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label="Select Employee"
                            name="employeeId"
                            rules={[{ required: true, message: "Please select an employee." }]}
                        >
                            <Select
                                placeholder="Select Employee"
                                allowClear
                                onChange={handleEmployeeChange}
                                disabled={!form.getFieldValue('storeId')}
                            >
                                {employees.map((employee) => (
                                    <Select.Option key={employee._id} value={employee._id}>
                                        {employee.name} - {employee.designation}
                                    </Select.Option>
                                ))}
                            </Select>
                             
                        </Form.Item>
                    </Col>
                </Row>

                {selectedEmployee && (
                    <Card size="small" style={{ marginBottom: "24px", backgroundColor: "#f8f9fa" }}>
                        <Row gutter={16}>
                            <Col span={8}>
                                <Text strong>Employee:</Text>
                                <br />
                                <Text>{selectedEmployee.name}</Text>
                            </Col>
                            <Col span={8}>
                                <Text strong>Designation:</Text>
                                <br />
                                <Text>{selectedEmployee.designation}</Text>
                            </Col>
                            <Col span={8}>
                                <Text strong>Current Salary:</Text>
                                <br />
                                <Text style={{ color: "#1890ff" }}>
                                    ₹{selectedEmployee.salary ? new Intl.NumberFormat().format(selectedEmployee.salary) : "Not Set"}
                                </Text>
                            </Col>
                        </Row>
                    </Card>
                )}

                <Row gutter={16}>

                <Col span={6}>
                    <Form.Item
                        label="Payment Date"
                        name="paymentDate"
                        rules={[{ required: true, message: "Please select payment date." }]}
                    >
                        <DatePicker style={{ width: "100%" }} />
                    </Form.Item>
                    
                </Col>

                <Col span={6}>
                    <Form.Item
                        label="Payment Type"
                        name="paymentType"
                        initialValue="Salary"
                    >
                        <Select
                            placeholder="Select Payment Type"
                            onChange={handlePaymentTypeChange}
                        >
                            <Option value="Salary">Salary</Option>
                            <Option value="Advance">Advance</Option>
                            <Option value="Incentive">Incentive</Option>
                        </Select>
                    </Form.Item>
                     
                </Col>
                    <Col span={6}>
                        <Form.Item
                            label="Month"
                            name="month"
                            rules={[{ required: true, message: "Please select month." }]}
                        >
                            <Select 
                                placeholder="Select Month"
                                onChange={async (value) => {
                                    // Check if selected month is in the future
                                    const currentDate = new Date();
                                    const currentYear = currentDate.getFullYear();
                                    const currentMonth = currentDate.getMonth();
                                    
                                    const selectedMonthIndex = monthOptions.indexOf(value);
                                    const selectedYear = form.getFieldValue('year') || currentYear;
                                    
                                    // If selected year is current year, check if month is future
                                    if (selectedYear === currentYear && selectedMonthIndex > currentMonth) {
                                        message.warning('Cannot select future months for current year');
                                        // Reset to current month
                                        form.setFieldsValue({ month: monthOptions[currentMonth] });
                                        return;
                                    }
                                    
                                    // If selected year is future year, check if it's allowed
                                    if (selectedYear > currentYear) {
                                        message.warning('Cannot select future years for salary payments');
                                        // Reset to current year
                                        form.setFieldsValue({ year: currentYear });
                                        return;
                                    }
                                    
                                    // Refresh advances for the new month/year if employee is selected
                                    if (selectedEmployee && selectedYear) {
                                        await refreshAdvancesForMonth(value, selectedYear);
                                    }
                                }}
                            >
                                {monthOptions.map((month) => {
                                    const currentDate = new Date();
                                    const currentYear = currentDate.getFullYear();
                                    const currentMonth = currentDate.getMonth();
                                    const selectedYear = form.getFieldValue('year') || currentYear;
                                    
                                    // Disable future months for current year
                                    const isDisabled = selectedYear === currentYear && 
                                                      monthOptions.indexOf(month) > currentMonth;
                                    
                                    return (
                                        <Option 
                                            key={month} 
                                            value={month}
                                            disabled={isDisabled}
                                        >
                                            {month} {isDisabled ? '(Restricted)' : ''}
                                        </Option>
                                    );
                                })}
                            </Select>
                        </Form.Item>
                         
                    </Col>
                    <Col span={6}>
                        <Form.Item
                            label="Year"
                            name="year"
                            rules={[{ required: true, message: "Please select year." }]}
                        >
                            <Select 
                                placeholder="Select Year"
                                onChange={async (value) => {
                                    // Check if selected year is in the future
                                    const currentYear = new Date().getFullYear();
                                    
                                    if (value > currentYear) {
                                        message.warning('Cannot select future years for salary payments');
                                        // Reset to current year
                                        form.setFieldsValue({ year: currentYear });
                                        return;
                                    }
                                    
                                    // When year changes, also validate month selection
                                    const currentMonth = new Date().getMonth();
                                    const selectedMonth = form.getFieldValue('month');
                                    
                                    if (value === currentYear && selectedMonth) {
                                        const selectedMonthIndex = monthOptions.indexOf(selectedMonth);
                                        if (selectedMonthIndex > currentMonth) {
                                            message.warning('Selected month is in the future for current year');
                                            // Reset to current month
                                            form.setFieldsValue({ month: monthOptions[currentMonth] });
                                        }
                                    }
                                    
                                    // Refresh advances for the new month/year if employee is selected
                                    if (selectedEmployee && selectedMonth) {
                                        await refreshAdvancesForMonth(selectedMonth, value);
                                    }
                                }}
                            >
                                {yearOptions.map((year) => {
                                    const currentYear = new Date().getFullYear();
                                    const isDisabled = year > currentYear;
                                    
                                    return (
                                        <Option 
                                            key={year} 
                                            value={year}
                                            disabled={isDisabled}
                                        >
                                            {year} {isDisabled ? '(Future Year)' : ''}
                                        </Option>
                                    );
                                })}
                            </Select>
                        </Form.Item>
                         
                    </Col>
                     
                </Row>

                {/* Outstanding Balance Information Card - Show when there are advances or previous balances */}
                {(existingAdvances > 0 || outstandingBalance > 0) && (
                    <Card size="small" style={{ marginBottom: "16px", backgroundColor: "#fff7e6", borderColor: "#faad14" }}>
                        <Row gutter={16}>
                            <Col span={6}>
                                <Text strong>Total Advances:</Text>
                                <br />
                                <Text style={{ color: "#faad14", fontSize: "16px" }}>
                                    ₹{new Intl.NumberFormat().format(existingAdvances)}
                                </Text>
                                <br />
                                <Text style={{ fontSize: "12px", color: "#666" }}>
                                    for {form.getFieldValue('month')} {form.getFieldValue('year')}
                                </Text>
                            </Col>
                            <Col span={6}>
                                <Text strong>Previous Balances:</Text>
                                <br />
                                <Text style={{ color: "#ff4d4f", fontSize: "16px" }}>
                                    ₹{new Intl.NumberFormat().format(outstandingBalance)}
                                </Text>
                            </Col>
                            <Col span={6}>
                                <Text strong>Total Outstanding:</Text>
                                <br />
                                <Text style={{ color: "#52c41a", fontSize: "16px", fontWeight: 'bold' }}>
                                    ₹{new Intl.NumberFormat().format(totalOutstanding)}
                                </Text>
                            </Col>
                            <Col span={6}>
                                <Text strong>Current Advance:</Text>
                                <br />
                                <Text style={{ color: "#1890ff", fontSize: "16px" }}>
                                    ₹{new Intl.NumberFormat().format(form.getFieldValue('advanceAmount') || 0)}
                                </Text>
                            </Col>
                        </Row>
                        
                        {/* Show previous month details if available */}
                        {previousMonthDetails.length > 0 && (
                            <div style={{ marginTop: "12px", padding: "8px", backgroundColor: "#f6ffed", borderRadius: "4px" }}>
                                <Text strong style={{ color: "#52c41a" }}>📋 Previous Month Balances:</Text>
                                <br />
                                {previousMonthDetails.map((detail, index) => (
                                    <Text key={index} style={{ fontSize: "12px", color: "#666" }}>
                                        {detail.month} {detail.year}: ₹{new Intl.NumberFormat().format(detail.balance)}
                                        {index < previousMonthDetails.length - 1 ? ', ' : ''}
                                    </Text>
                                ))}
                            </div>
                        )}
                    </Card>
                )}

                {paymentType === 'Salary' && (
                    <>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="Total Days in Month"
                                    name="totalDays"
                                    initialValue={30}
                                    rules={[{ required: true, message: "Please enter total days." }]}
                                >
                                    <Input 
                                        type="number" 
                                        value={30}
                                        disabled
                                        style={{ backgroundColor: '#f5f5f5' }}
                                        onChange={calculateSalary}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label="Present Days"
                                    name="presentDays"
                                    rules={[
                                        { required: true, message: "Please enter present days." },
                                        {
                                            validator: (_, value) => {
                                                if (value && value > 30) {
                                                    return Promise.reject(new Error('Present days cannot exceed 30 days'));
                                                }
                                                if (value && value < 0) {
                                                    return Promise.reject(new Error('Present days cannot be negative'));
                                                }
                                                return Promise.resolve();
                                            }
                                        }
                                    ]}
                                >
                                    <Input 
                                        type="number" 
                                        placeholder="e.g., 25"
                                        min={0}
                                        max={30}
                                        onChange={calculateSalary}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={24}>
                                <Form.Item
                                    label="Deduction Amount (Absent Days)"
                                    name="deductionAmount"
                                >
                                    <Input 
                                        type="number" 
                                        placeholder="Auto-calculated"
                                        value={form.getFieldValue('deductionAmount') || 0}
                                        disabled
                                        style={{ backgroundColor: '#f5f5f5' }}
                                    />
                                    <div style={{ marginTop: "4px" }}>
                                        <Text type="secondary" style={{ fontSize: "12px" }}>
                                            Automatically calculated based on absent days
                                        </Text>
                                        <div style={{ marginTop: "4px" }}>
                                            <Text type="secondary" style={{ fontSize: "12px", color: "#52c41a" }}>
                                                💡 New advances for {form.getFieldValue('month')} {form.getFieldValue('year')} and previous month balances are automatically calculated and shown in the breakdown below
                                            </Text>
                                            <div style={{ marginTop: "4px" }}>
                                                <Text type="secondary" style={{ fontSize: "11px", color: "#666" }}>
                                                    📋 <strong>Calculation:</strong> Present Days Salary - Absent Days Deduction - New Advances + Previous Balance
                                                </Text>
                                            </div>
                                        </div>
                                    </div>
                                </Form.Item>
                            </Col>


                        </Row>
                    </>
                )}

                {paymentType === 'Advance' && (
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                label="Advance Amount"
                                name="advanceAmount"
                                rules={[{ required: true, message: "Please enter advance amount." }]}
                            >
                                <Input 
                                    type="number" 
                                    placeholder="Enter advance amount"
                                    onChange={handleAdvanceAmountChange}
                                />
                            </Form.Item>
                            <div style={{ marginTop: "8px" }}>
                                <Text type="secondary" style={{ fontSize: "12px", color: "#52c41a" }}>
                                    💡 Advance payments are processed with payment method tracking
                                </Text>
                            </div>
                        </Col>
                    </Row>
                )}

                {paymentType === 'Incentive' && (
                    <Row gutter={24}>
                        <Col span={24}>
                            <Form.Item
                                label="Incentive Amount"
                                name="incentiveAmount"
                                rules={[{ required: true, message: "Please enter incentive amount." }]}
                            >
                                <Input 
                                    type="number" 
                                    placeholder="Enter incentive amount"
                                />
                            </Form.Item>
                            <div style={{ marginTop: "8px" }}>
                                <Text type="secondary" style={{ fontSize: "12px", color: "#52c41a" }}>
                                    💡 Incentive payments are processed with payment method tracking
                                </Text>
                            </div>
                        </Col>
                    </Row>
                )}

                {calculatedSalary > 0 && paymentType === 'Salary' && (
                    <Card size="small" style={{ marginBottom: "16px", backgroundColor: "#e6f7ff", borderColor: "#1890ff" }}>
                        <Row gutter={16}>
                            <Col span={6}>
                                <Text strong>Monthly Salary:</Text>
                                <br />
                                <Title level={4} style={{ color: "#1890ff", margin: "8px 0" }}>
                                    ₹{new Intl.NumberFormat().format(selectedEmployee?.salary || 0)}
                                </Title>
                            </Col>
                            <Col span={6}>
                                <Text strong>Present Days Salary:</Text>
                                <br />
                                <Title level={4} style={{ color: "#52c41a", margin: "8px 0" }}>
                                    ₹{new Intl.NumberFormat().format(calculatedSalary)}
                                </Title>
                            </Col>
                            <Col span={6}>
                                <Text strong>Absent Days Deduction:</Text>
                                <br />
                                <Text style={{ color: "#ff4d4f", fontSize: "16px" }}>
                                    ₹{new Intl.NumberFormat().format(form.getFieldValue('deductionAmount') || 0)}
                                </Text>
                            </Col>
                            <Col span={6}>
                                <Text strong>New Advances ({form.getFieldValue('month')}):</Text>
                                <br />
                                <Text style={{ color: "#faad14", fontSize: "16px" }}>
                                    ₹{new Intl.NumberFormat().format(existingAdvances)}
                                </Text>
                            </Col>
                        </Row>
                        
                        {/* Additional row for previous balance if exists */}
                        {outstandingBalance > 0 && (
                            <Row gutter={16} style={{ marginTop: "12px" }}>
                                <Col span={6}>
                                    <Text strong>Previous Balance:</Text>
                                    <br />
                                    <Text style={{ color: "#ff4d4f", fontSize: "16px" }}>
                                        ₹{new Intl.NumberFormat().format(outstandingBalance)}
                                    </Text>
                                </Col>
                                <Col span={6}>
                                    <Text strong>Balance Impact:</Text>
                                    <br />
                                    <Text style={{ color: "#52c41a", fontSize: "16px" }}>
                                        + ₹{new Intl.NumberFormat().format(outstandingBalance)}
                                    </Text>
                                </Col>
                                <Col span={12}>
                                    <Text strong>Note:</Text>
                                    <br />
                                    <Text style={{ color: "#666", fontSize: "12px" }}>
                                        Previous month's unpaid balance is added to current month's salary
                                    </Text>
                                </Col>
                            </Row>
                        )}
                    </Card>
                )}

                {/* Final Net Amount Display */}
                {calculatedSalary > 0 && paymentType === 'Salary' && (
                    <Card size="small" style={{ marginBottom: "16px", backgroundColor: "#f6ffed", borderColor: "#52c41a" }}>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Text strong>Net Payable Amount:</Text>
                                <br />
                                <Title level={3} style={{ color: "#52c41a", margin: "8px 0" }}>
                                    ₹{new Intl.NumberFormat().format(remainingAmount)}
                                </Title>
                                {(existingAdvances > 0 || outstandingBalance > 0) && (
                                    <div style={{ marginTop: "8px" }}>
                                        <Text type="secondary" style={{ fontSize: "12px" }}>
                                            {existingAdvances > 0 && `New advances: ₹${new Intl.NumberFormat().format(existingAdvances)}`}
                                            {existingAdvances > 0 && outstandingBalance > 0 && ' • '}
                                            {outstandingBalance > 0 && `Previous balance: +₹${new Intl.NumberFormat().format(outstandingBalance)}`}
                                        </Text>
                                    </div>
                                )}
                            </Col>
                            <Col span={12}>
                                <Text strong>Calculation Breakdown:</Text>
                                <br />
                                <Text style={{ fontSize: "14px", lineHeight: "1.5" }}>
                                    Present Days Salary: ₹{new Intl.NumberFormat().format(calculatedSalary)}<br />
                                    - Absent Days Deduction: ₹{new Intl.NumberFormat().format(form.getFieldValue('deductionAmount') || 0)}<br />
                                    {existingAdvances > 0 && (
                                        <>
                                            - New Advances: ₹{new Intl.NumberFormat().format(existingAdvances)}<br />
                                        </>
                                    )}
                                    {outstandingBalance > 0 && (
                                        <>
                                            + Previous Balance: ₹{new Intl.NumberFormat().format(outstandingBalance)}<br />
                                        </>
                                    )}
                                    = Net Payable: ₹{new Intl.NumberFormat().format(remainingAmount)}
                                </Text>
                            </Col>
                        </Row>
                    </Card>
                )}

                {/* Payment Details Section - Only for Salary payments */}
                {paymentType === 'Salary' && (
                    <Card size="small" style={{ marginBottom: "24px", backgroundColor: "#fff7e6", borderColor: "#faad14" }}>
                        <div style={{ marginBottom: "16px" }}>
                            <Title level={4} style={{ color: "#faad14", margin: 0 }}>
                                💰 Salary Payment Details
                            </Title>
                        </div>
                        
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item
                                    label="Paid Amount"
                                    name="paidAmount"
                                    initialValue={remainingAmount}
                                    rules={[
                                        { 
                                            required: paymentType === 'Salary', 
                                            message: "Please enter paid amount." 
                                        },
                                        {
                                            validator: (_, value) => {
                                                // Only validate for Salary payments
                                                if (paymentType !== 'Salary') {
                                                    return Promise.resolve();
                                                }
                                                
                                                if (!value || value <= 0) {
                                                    return Promise.reject(new Error('Paid amount must be greater than 0'));
                                                }
                                                if (value > remainingAmount) {
                                                    return Promise.reject(new Error(`Paid amount cannot exceed net payable amount (₹${remainingAmount})`));
                                                }
                                                return Promise.resolve();
                                            }
                                        }
                                    ]}
                                >
                                    <Input 
                                        type="number" 
                                        placeholder="Enter amount to pay"
                                        min={0}
                                        max={remainingAmount}
                                        onChange={handlePaidAmountChange}
                                        style={{ fontSize: "16px", fontWeight: "bold" }}
                                    />
                                    <div style={{ marginTop: "4px" }}>
                                        <Text type="secondary" style={{ fontSize: "12px" }}>
                                            {remainingAmount > 0 ? 
                                                `Defaults to Net Payable Amount: ₹${new Intl.NumberFormat().format(remainingAmount)} • You can modify this amount` :
                                                'Will default to Net Payable Amount when calculated'
                                            }
                                        </Text>
                                    </div>
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    label="Balance Amount"
                                    name="balanceAmount"
                                >
                                    <Input 
                                        type="number" 
                                        placeholder="Auto-calculated"
                                        value={form.getFieldValue('balanceAmount') || 0}
                                        disabled
                                        style={{ 
                                            backgroundColor: '#f5f5f5',
                                            fontSize: "16px",
                                            fontWeight: "bold",
                                            color: form.getFieldValue('balanceAmount') > 0 ? '#ff4d4f' : '#52c41a'
                                        }}
                                    />
                                </Form.Item>
                            </Col>

                        </Row>



                        {/* Payment Summary */}
                        {form.getFieldValue('paidAmount') > 0 && (
                            <Row gutter={16}>
                                <Col span={24}>
                                    <div style={{ 
                                        padding: "12px", 
                                        backgroundColor: "#f0f9ff", 
                                        borderRadius: "6px",
                                        border: "1px solid #91d5ff"
                                    }}>
                                        <Row gutter={16}>
                                            <Col span={6}>
                                                <Text strong>Net Payable:</Text>
                                                <br />
                                                <Text style={{ color: "#1890ff", fontSize: "16px" }}>
                                                    ₹{new Intl.NumberFormat().format(remainingAmount)}
                                                </Text>
                                            </Col>
                                            <Col span={6}>
                                                <Text strong>Paid Amount:</Text>
                                                <br />
                                                <Text style={{ color: "#52c41a", fontSize: "16px" }}>
                                                    ₹{new Intl.NumberFormat().format(form.getFieldValue('paidAmount') || 0)}
                                                </Text>
                                            </Col>
                                            <Col span={6}>
                                                <Text strong>Balance Amount:</Text>
                                                <br />
                                                <Text style={{ 
                                                    color: form.getFieldValue('balanceAmount') > 0 ? "#ff4d4f" : "#52c41a", 
                                                    fontSize: "16px",
                                                    fontWeight: "bold"
                                                }}>
                                                    ₹{new Intl.NumberFormat().format(form.getFieldValue('balanceAmount') || 0)}
                                                </Text>
                                            </Col>
                                            <Col span={6}>
                                                <Text strong>Payment Mode:</Text>
                                                <br />
                                                <Text style={{ color: "#722ed1", fontSize: "16px" }}>
                                                    {form.getFieldValue('paymentMode') || 'Not selected'}
                                                </Text>
                                            </Col>
                                        </Row>
                                    </div>
                                </Col>
                            </Row>
                        )}
                    </Card>
                )}

                {/* Common Payment Fields - For All Payment Types */}
                <Card size="small" style={{ marginBottom: "24px", backgroundColor: "#f0f9ff", borderColor: "#1890ff" }}>
                    <div style={{ marginBottom: "16px" }}>
                        <Title level={4} style={{ color: "#1890ff", margin: 0 }}>
                            💳 Payment Method & Reference
                        </Title>
                    </div>
                    
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Payment Mode"
                                name="paymentMode"
                                rules={[{ required: true, message: "Please select payment mode." }]}
                            >
                                <Select 
                                    placeholder="Select payment mode"
                                    defaultValue="Cash"
                                    onChange={(value) => {
                                        form.setFieldsValue({ paymentMode: value });
                                    }}
                                >
                                    <Option value="Cash">💵 Cash</Option>
                                    <Option value="UPI">📱 UPI</Option>
                                    <Option value="Cheque">🏦 Cheque</Option>
                                    <Option value="Bank Transfer">🏛️ Bank Transfer</Option>
                                    <Option value="Online Payment">💻 Online Payment</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Reference Number"
                                name="referenceNumber"
                            >
                                <Input 
                                    placeholder="UPI ID, Cheque No, Transaction ID, etc."
                                    onChange={handleReferenceNumberChange}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>

                <Form.Item
                    label="Remarks"
                    name="remarks"
                >
                    <Input.TextArea rows={3} placeholder="Additional notes or comments..." />
                </Form.Item>

                {/* Debug Information */}
                {process.env.NODE_ENV === 'development' && (
                    <div style={{ 
                        marginTop: "16px", 
                        padding: "12px", 
                        backgroundColor: "#f0f0f0", 
                        borderRadius: "4px",
                        fontSize: "11px",
                        fontFamily: "monospace"
                    }}>
                        <Text strong>Debug Info:</Text>
                        <br />
                        <Text>Payment Status: {paymentStatus}</Text>
                        <br />
                        <Text>Loading: {loading.toString()}</Text>
                        <br />
                        <Text>Submission Attempts: {submissionAttempts}</Text>
                        <br />
                        <Text>Employee ID: {selectedEmployee?._id || 'None'}</Text>
                        <Text>Store ID: {form.getFieldValue('storeId') || 'None'}</Text>
                    </div>
                )}
                

                <Form.Item>
                    <Button 
                        type="primary" 
                        htmlType="submit" 
                        loading={loading}
                        disabled={loading}
                        size="large"
                        style={{ 
                            height: "40px", 
                            fontSize: "16px", 
                            fontWeight: "bold",
                            backgroundColor: "#52c41a",
                            borderColor: "#52c41a"
                        }}
                    >
                        {loading ? "Processing Payment..." : "Process Payment"}
                    </Button>
                    
                    {/* Payment Status Indicator */}
                    {paymentStatus !== 'idle' && (
                        <div style={{ 
                            marginTop: "8px", 
                            textAlign: "center",
                            fontSize: "12px",
                            padding: "8px",
                            borderRadius: "4px",
                            backgroundColor: paymentStatus === 'processing' ? '#e6f7ff' : 
                                           paymentStatus === 'success' ? '#f6ffed' : '#fff2f0',
                            color: paymentStatus === 'processing' ? '#1890ff' : 
                                   paymentStatus === 'success' ? '#52c41a' : '#ff4d4f',
                            border: `1px solid ${paymentStatus === 'processing' ? '#91d5ff' : 
                                                   paymentStatus === 'success' ? '#b7eb8f' : '#ffccc7'}`
                        }}>
                            {paymentStatus === 'processing' && '⏳ Processing payment...'}
                            {paymentStatus === 'success' && '✅ Payment processed successfully!'}
                            {paymentStatus === 'failed' && '❌ Payment failed. Please try again.'}
                        </div>
                    )}
                </Form.Item>
                    </Form>
                </Col>
                
                {/* Right Side - Payment History Table */}
                <Col span={10}>
                    {selectedEmployee && (
                        <Card 
                            title={`📊 Payments - ${selectedEmployee.name}`}
                            size="small"
                            style={{ height: 'fit-content' }}
                            extra={
                                <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
                                     
                                     
                                    <Select
                                        defaultValue="all"
                                        style={{ width: 200, fontSize: '12px' }}
                                        size="small"
                                        onChange={(value) => {
                                            if (value === 'all') {
                                                fetchPaymentHistory(selectedEmployee._id);
                                            } else {
                                                const filtered = paymentHistory.filter(payment => payment.paymentType === value);
                                                setPaymentHistory(filtered);
                                            }
                                        }}
                                    >
                                        <Option value="all">All Types</Option>
                                        <Option value="Salary">Salary</Option>
                                        <Option value="Advance">Advance</Option>
                                        <Option value="Incentive">Incentive</Option>
                                    </Select>
                                </div>
                            }
                        >
                            {paymentHistoryLoading ? (
                                <div style={{ textAlign: 'center', padding: '20px' }}>
                                    <Text type="secondary">Loading payment history...</Text>
                                </div>
                            ) : paymentHistory.length > 0 ? (
                                <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                                    {/* Header Row */}
                                    <div style={{ 
                                        display: 'grid', 
                                        gridTemplateColumns: '0.8fr 1fr 1fr 1fr 1fr 1fr 1fr 1.2fr',
                                        gap: '8px',
                                        padding: '10px 12px',
                                        backgroundColor: '#1890ff',
                                        borderRadius: '4px',
                                        marginBottom: '12px',
                                        fontSize: '11px',
                                        fontWeight: 'bold',
                                        color: '#ffffff',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        <div>Type</div>
                                        <div>Salary Amount</div>
                                        <div>Paid Amount</div>
                                        <div>Balance (Salary Only)</div>
                                        <div>Running Balance (Salary)</div>
                                        <div>Month Year</div>
                                        <div>Payment Date</div>
                                        <div>Remarks</div>
                                    </div>
                                    
                                    {/* Payment Rows */}
                                    {paymentHistory.map((payment, index) => (
                                        <div 
                                            key={payment._id || index} 
                                            style={{ 
                                                display: 'grid', 
                                                gridTemplateColumns: '0.8fr 1fr 1fr 1fr 1fr 1fr 1fr 1.2fr',
                                                gap: '4px',
                                                padding: '8px 12px',
                                                borderBottom: '1px solid #f0f0f0',
                                                fontSize: '11px',
                                                alignItems: 'center',
                                                backgroundColor: index % 2 === 0 ? '#fafafa' : '#ffffff',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                borderRadius: '4px',
                                                margin: '2px 0'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.target.parentElement.style.backgroundColor = '#f0f8ff';
                                                e.target.parentElement.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.parentElement.style.backgroundColor = index % 2 === 0 ? '#fafafa' : '#ffffff';
                                                e.target.parentElement.style.boxShadow = 'none';
                                            }}
                                        >
                                            {/* Payment Type */}
                                            <div style={{ 
                                                fontWeight: 'bold',
                                                color: payment.paymentType === 'Salary' ? '#52c41a' : 
                                                       payment.paymentType === 'Advance' ? '#faad14' : '#1890ff',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px'
                                            }}>
                                                <span style={{ 
                                                    width: '8px', 
                                                    height: '8px', 
                                                    borderRadius: '50%',
                                                    backgroundColor: payment.paymentType === 'Salary' ? '#52c41a' : 
                                                                   payment.paymentType === 'Advance' ? '#faad14' : '#1890ff'
                                                }}></span>
                                                {payment.paymentType}
                                            </div>
                                            
                                            {/* Salary Amount */}
                                            <div style={{ 
                                                fontWeight: 'bold',
                                                color: payment.paymentType === 'Salary' ? '#52c41a' : '#666'
                                            }}>
                                                {payment.paymentType === 'Salary' ? 
                                                    `₹${new Intl.NumberFormat().format(payment.net_salary || 0)}` :
                                                    payment.paymentType === 'Advance' ? 
                                                        '₹0' :
                                                    payment.paymentType === 'Incentive' ? 
                                                        '₹0' :
                                                    '-'
                                                }
                                            </div>
                                            
                                            {/* Paid Amount */}
                                            <div style={{ 
                                                fontWeight: 'bold',
                                                color: payment.paymentType === 'Salary' ? '#52c41a' : 
                                                       payment.paymentType === 'Advance' ? '#faad14' : '#1890ff'
                                            }}>
                                                {payment.paymentType === 'Salary' ? 
                                                    `₹${new Intl.NumberFormat().format(payment.paid_amount || 0)}` :
                                                    payment.paymentType === 'Advance' ? 
                                                        `₹${new Intl.NumberFormat().format(payment.advance_amount || 0)}` :
                                                    payment.paymentType === 'Incentive' ? 
                                                        `₹${new Intl.NumberFormat().format(payment.incentive_amount || 0)}` :
                                                    '-'
                                                }
                                            </div>
                                            
                                            {/* Balance Amount */}
                                            <div style={{ 
                                                fontWeight: 'bold',
                                                color: payment.paymentType === 'Salary' ? 
                                                    (payment.balance_amount > 0 ? '#ff4d4f' : '#52c41a') : '#999'
                                            }}>
                                                {payment.paymentType === 'Salary' ? 
                                                    `₹${new Intl.NumberFormat().format(payment.balance_amount || 0)}` :
                                                    payment.paymentType === 'Advance' ? 
                                                        '0' :
                                                    payment.paymentType === 'Incentive' ? 
                                                        '0' : '-'
                                                }
                                            </div>
                                            
                                            {/* Running Balance */}
                                            <div style={{ 
                                                fontWeight: 'bold',
                                                color: '#1890ff',
                                                fontSize: '10px'
                                            }}>
                                                {payment.paymentType === 'Salary' ? 
                                                    `₹${new Intl.NumberFormat().format(
                                                        paymentHistory
                                                            .slice(0, index + 1)
                                                            .filter(p => p.paymentType === 'Salary')
                                                            .reduce((sum, p) => sum + (p.balance_amount || 0), 0)
                                                    )}` :
                                                    payment.paymentType === 'Advance' ? 
                                                        'N/A' :
                                                    payment.paymentType === 'Incentive' ? 
                                                        'N/A' : '-'
                                                }
                                            </div>
                                            
                                            {/* Month Year */}
                                            <div style={{ color: '#666' }}>
                                                {payment.for_the_month} {payment.year}
                                            </div>
                                            
                                            {/* Payment Date */}
                                            <div style={{ color: '#666' }}>
                                                {new Date(payment.payment_date).toLocaleDateString('en-IN', {
                                                    day: '2-digit',
                                                    month: '2-digit',
                                                    year: '2-digit'
                                                })}
                                            </div>
                                            
                                            {/* Remarks */}
                                            <div style={{ 
                                                color: '#666',
                                                fontSize: '10px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}>
                                                {payment.remarks || 
                                                    (payment.paymentType === 'Salary' ? 
                                                        `Present: ${payment.present_days || 0}/${payment.total_days || 0} days` :
                                                    payment.paymentType === 'Advance' ? 
                                                        `Advance payment` :
                                                    payment.paymentType === 'Incentive' ? 
                                                        `Incentive payment` : '-'
                                                    )
                                                }
                                            </div>
                                        </div>
                                    ))}
                                    
                                    {/* Summary Row */}
                                    <div style={{ 
                                        display: 'grid', 
                                        gridTemplateColumns: '0.8fr 1fr 1fr 1fr 1fr 1fr 1fr 1.2fr',
                                        gap: '8px',
                                        padding: '12px',
                                        backgroundColor: '#f6ffed',
                                        borderRadius: '4px',
                                        marginTop: '12px',
                                        border: '2px solid #52c41a',
                                        fontSize: '12px',
                                        fontWeight: 'bold'
                                    }}>
                                        <div style={{ color: '#52c41a' }}>TOTALS</div>
                                        <div style={{ color: '#52c41a' }}>
                                            ₹{new Intl.NumberFormat().format(
                                                paymentHistory.reduce((sum, payment) => {
                                                    if (payment.paymentType === 'Salary') return sum + (payment.net_salary || 0);
                                                    return sum;
                                                }, 0)
                                            )}
                                        </div>
                                        <div style={{ color: '#52c41a' }}>
                                            ₹{new Intl.NumberFormat().format(
                                                paymentHistory.reduce((sum, payment) => {
                                                    if (payment.paymentType === 'Salary') return sum + (payment.paid_amount || 0);
                                                    if (payment.paymentType === 'Advance') return sum + (payment.advance_amount || 0);
                                                    if (payment.paymentType === 'Incentive') return sum + (payment.incentive_amount || 0);
                                                    return sum;
                                                }, 0)
                                            )}
                                        </div>
                                        <div style={{ color: '#ff4d4f' }}>
                                            ₹{new Intl.NumberFormat().format(
                                                paymentHistory.reduce((sum, payment) => {
                                                    if (payment.paymentType === 'Salary') return sum + (payment.balance_amount || 0);
                                                    return sum;
                                                }, 0)
                                            )}
                                        </div>
                                        <div style={{ color: '#1890ff' }}>
                                            ₹{new Intl.NumberFormat().format(
                                                paymentHistory.reduce((sum, payment) => {
                                                    if (payment.paymentType === 'Salary') return sum + (payment.balance_amount || 0);
                                                    return sum;
                                                }, 0)
                                            )}
                                        </div>
                                        <div style={{ color: '#52c41a' }}>-</div>
                                        <div style={{ color: '#52c41a' }}>-</div>
                                        <div style={{ color: '#52c41a' }}>-</div>
                                    </div>
                                </div>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '20px' }}>
                                    <Text type="secondary">No payment history found</Text>
                                </div>
                            )}
                        </Card>
                    )}
                    
                    {!selectedEmployee && (
                        <Card 
                            title="Payment Records" 
                            size="small"
                            style={{ height: 'fit-content' }}
                        >
                            <div style={{ textAlign: 'center', padding: '20px' }}>
                                <Text type="secondary">Select an employee to view payment history</Text>
                            </div>
                        </Card>
                    )}
                </Col>
            </Row>
        </div>
    );
};

export default EmployeeSalary;
